import { ACCEPTED_UPLOAD_MIME, isNoteRequired, isPhotoRequired, noticesFor } from '@nvd/claim-shared';
import { useState, type DragEvent } from 'react';
import { ClaimedPills } from '../components/ClaimedItems';
import { Icon } from '../components/Icon';
import { Spinner } from '../components/Spinner';
import { StepLayout } from '../components/StepLayout';
import { compressImage } from '../lib/imageCompress';
import { UI } from '../lib/ui';
import { useAction, usePortal } from '../Portal';
import { buildSubmitRequest, reasonIdsOf } from '../state/machine';

const MAX_FILE_BYTES = 8 * 1024 * 1024;
const ACCEPT = [...ACCEPTED_UPLOAD_MIME, '.heic', '.pdf'].join(',');

export function IssueDetails() {
  const { config, t, api, state, dispatch } = usePortal();
  const { notes, uploads } = state.draft;
  const reasonIds = reasonIdsOf(state.draft);
  const notesRequired = isNoteRequired(config, reasonIds);
  const photosRequired = isPhotoRequired(config, reasonIds);
  const notices = noticesFor(config, reasonIds);
  const maxUploads = config.intake.photos.max;

  const [uploading, setUploading] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const upload = useAction();
  const submit = useAction();
  // Without a resolution step this is the last screen, so Next sends the claim.
  const sendsClaim = !config.intake.chooseResolution;

  const addFiles = (files: File[]) => {
    if (uploads.length + uploading + files.length > maxUploads) {
      setError(UI.uploadLimit(maxUploads));
      return;
    }
    setError(null);
    for (const original of files) {
      setUploading((count) => count + 1);
      void upload
        .run(async () => {
          const file = await compressImage(original);
          if (file.size > MAX_FILE_BYTES) {
            setError(UI.uploadTooLarge(original.name));
            return;
          }
          const result = await api.upload(file, file.name);
          const previewUrl = file.type.startsWith('image/') && file.type !== 'image/heic' ? URL.createObjectURL(file) : undefined;
          dispatch({ type: 'ADD_UPLOAD', upload: { ...result, previewUrl } });
        })
        .finally(() => setUploading((count) => count - 1));
    }
  };

  const full = uploads.length + uploading >= maxUploads;
  const photosOff = config.intake.photos.mode === 'off';
  const notesOff = config.intake.note === 'off' && !notesRequired;

  const drop = (event: DragEvent) => {
    event.preventDefault();
    setDragging(false);
    if (!full) addFiles(Array.from(event.dataTransfer.files));
  };

  const removeUpload = (uploadId: string, previewUrl?: string) => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    dispatch({ type: 'REMOVE_UPLOAD', uploadId });
  };

  const next = () => {
    if (notesRequired && !notes.trim()) {
      setError(UI.notesRequired);
      return;
    }
    if (photosRequired && uploads.length < Math.max(1, config.intake.photos.min)) {
      setError(UI.uploadRequired);
      return;
    }
    if (!sendsClaim) {
      dispatch({ type: 'GO', step: 'resolution' });
      return;
    }
    void submit.run(async () => {
      const { outcome } = await api.submit(buildSubmitRequest(state.draft));
      dispatch({ type: 'SUBMITTED', outcome });
    });
  };

  const required = <span className="nvd-claim-portal__required"> *</span>;
  const optional = <span className="nvd-claim-portal__optional"> {t('issue.optional')}</span>;

  return (
    <StepLayout
      title={t('issue.title')}
      error={error ?? upload.error ?? submit.error}
      busy={submit.busy}
      onBack={() => dispatch({ type: 'BACK' })}
      onNext={next}
      nextLabel={sendsClaim ? t('common.submit') : undefined}
      nextDisabled={uploading > 0}
    >
      <ClaimedPills />

      {notices.map((notice) => (
        <div key={notice.text} className={`nvd-claim-portal__notice nvd-claim-portal__notice--${notice.tone}`} role="note">
          <span className="nvd-claim-portal__notice-icon" aria-hidden="true">
            <Icon name="info" />
          </span>
          <p>{notice.text}</p>
        </div>
      ))}

      {!notesOff && (
        <label className="nvd-claim-portal__field">
          <span className="nvd-claim-portal__label">
            {t('issue.noteLabel')}
            {notesRequired ? required : optional}
          </span>
          <textarea
            className="nvd-claim-portal__input nvd-claim-portal__textarea"
            rows={4}
            maxLength={2000}
            placeholder={t('issue.notePlaceholder')}
            value={notes}
            onChange={(event) => dispatch({ type: 'SET_NOTES', notes: event.target.value })}
          />
        </label>
      )}

      {!photosOff && (
        <div className="nvd-claim-portal__field">
          <span className="nvd-claim-portal__label">
            {t('issue.photosLabel')}
            {photosRequired ? required : optional}
            {config.intake.helpLink && (
              <>
                {' '}
                <a className="nvd-claim-portal__link" href={config.intake.helpLink.url} target="_blank" rel="noopener noreferrer">
                  {config.intake.helpLink.label}
                </a>
              </>
            )}
          </span>
          {(uploads.length > 0 || uploading > 0) && (
            <ul className="nvd-claim-portal__uploads">
              {uploads.map((file) => (
                <li key={file.uploadId} className="nvd-claim-portal__upload-item">
                  {file.previewUrl ? (
                    <img className="nvd-claim-portal__upload-thumb" src={file.previewUrl} alt="" />
                  ) : (
                    <span className="nvd-claim-portal__upload-thumb nvd-claim-portal__upload-thumb--file">
                      <Icon name="file" />
                    </span>
                  )}
                  <span className="nvd-claim-portal__upload-name">{file.name}</span>
                  <button
                    type="button"
                    className="nvd-claim-portal__upload-remove"
                    aria-label={`${UI.remove} ${file.name}`}
                    onClick={() => removeUpload(file.uploadId, file.previewUrl)}
                  >
                    <Icon name="close" />
                  </button>
                </li>
              ))}
              {uploading > 0 && (
                <li className="nvd-claim-portal__upload-item">
                  <span className="nvd-claim-portal__upload-thumb nvd-claim-portal__upload-thumb--file">
                    <Spinner small />
                  </span>
                  <span className="nvd-claim-portal__upload-name">{UI.uploading}</span>
                </li>
              )}
            </ul>
          )}

          {!full && (
            <label
              className={`nvd-claim-portal__upload${dragging ? ' nvd-claim-portal__upload--dragging' : ''}`}
              onDragOver={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={drop}
            >
              <input
                className="nvd-claim-portal__sr-only"
                type="file"
                multiple
                accept={ACCEPT}
                onChange={(event) => {
                  addFiles(Array.from(event.target.files ?? []));
                  event.target.value = '';
                }}
              />
              <span className="nvd-claim-portal__upload-icon">
                <Icon name="upload" />
              </span>
              <span className="nvd-claim-portal__upload-text">
                <span className="nvd-claim-portal__upload-cta">{uploads.length > 0 ? t('issue.dropzoneMore') : t('issue.dropzone')}</span>
                <span className="nvd-claim-portal__upload-hint">{t('issue.dropzoneHint')}</span>
              </span>
            </label>
          )}
        </div>
      )}
    </StepLayout>
  );
}
